import React, { useState, useEffect } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useFamilyContext } from '../../../../contexts/FamilyContext';
import { supabase } from '../../../../lib/supabase';
import { toast } from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import {
  FileText,
  Award,
  MessageSquare,
  Eye,
  Download,
  User,
  Send,
  MessageCircle,
  X,
  Printer
} from 'lucide-react';
import ProfessionalBragSheetView from '../../ProfessionalBragSheetView';

const SharedDocumentsSection = () => {
  const { selectedStudent, sharedItems, refreshData } = useParentPortal();
  const { currentUser } = useFamilyContext();

  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showFullBragSheet, setShowFullBragSheet] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [enrichedItems, setEnrichedItems] = useState([]);

  // Load and enrich shared items with their actual content
  useEffect(() => {
    if (sharedItems && sharedItems.length > 0) {
      enrichSharedItems();
    } else {
      setEnrichedItems([]);
    }
  }, [sharedItems]);

  const enrichSharedItems = async () => {
    setLoading(true);
    try {
      const enriched = await Promise.all(sharedItems.map(async (item) => {
        let content = null;
        let itemData = {};

        try {
          switch (item.item_type) {
            case 'essay':
              const { data: essay } = await supabase
                .from('college_journey_essays')
                .select(`
                  *,
                  college:college_journey_colleges(
                    name,
                    category
                  )
                `)
                .eq('id', item.item_id)
                .single();
              itemData = essay || {};
              content = essay?.current_draft || essay?.content || '';
              break;

            case 'teacher_brag_sheet':
              const { data: bragSheet } = await supabase
                .from('college_recommendation_letters')
                .select('*, brag_sheet_data')
                .eq('id', item.item_id)
                .single();
              itemData = bragSheet || {};
              content = bragSheet?.brag_sheet_data || {};
              break;

            case 'brag_sheet':
              const { data: profileData } = await supabase
                .from('student_academic_profiles')
                .select('brag_sheet_data')
                .eq('user_id', item.student?.auth_user_id)
                .single();
              itemData = { brag_sheet_data: profileData?.brag_sheet_data };
              content = profileData?.brag_sheet_data || {};
              break;

            case 'recommendation':
              const { data: rec } = await supabase
                .from('college_recommendation_letters')
                .select('*')
                .eq('id', item.item_id)
                .single();
              itemData = rec || {};
              content = rec;
              break;

            default:
              content = null;
          }

          // Get comments for this item
          const { data: comments } = await supabase
            .from('parent_comments')
            .select(`
              *,
              parent:family_members!parent_id(
                name
              ),
              author_type,
              author_id
            `)
            .eq('sharing_permission_id', item.id)
            .order('created_at', { ascending: true });

          return {
            ...item,
            itemData,
            content,
            comments: comments || []
          };
        } catch (error) {
          console.error(`Error loading content for ${item.item_type}:`, error);
          return {
            ...item,
            itemData: {},
            content: null,
            comments: []
          };
        }
      }));

      setEnrichedItems(enriched);
    } catch (error) {
      console.error('Error enriching shared items:', error);
      toast.error('Failed to load shared items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (permissionId) => {
    const text = commentText[permissionId];
    if (!text?.trim()) return;

    try {
      const { data: parentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (!parentMember) {
        toast.error('Unable to add comment - parent record not found');
        return;
      }

      const { error } = await supabase
        .from('parent_comments')
        .insert({
          sharing_permission_id: permissionId,
          parent_id: parentMember.id,
          comment_text: text.trim(),
          author_type: 'parent',
          author_id: parentMember.id
        });

      if (error) throw error;

      toast.success('Comment added successfully');
      setCommentText(prev => ({ ...prev, [permissionId]: '' }));

      // Refresh the data to show new comment
      await enrichSharedItems();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const renderBragSheetContent = (bragSheetData) => {
    if (!bragSheetData) return <p className="text-stone-500">No brag sheet data available</p>;

    return (
      <div className="space-y-4">
        {bragSheetData.achievements && bragSheetData.achievements.length > 0 && (
          <div>
            <h4 className="font-semibold text-stone-700 mb-2">Achievements</h4>
            <ul className="list-disc list-inside space-y-1">
              {bragSheetData.achievements.slice(0, 3).map((achievement, idx) => (
                <li key={idx} className="text-sm text-stone-600">
                  {typeof achievement === 'object' ?
                    (achievement.title || achievement.name || achievement.description || JSON.stringify(achievement)) :
                    achievement}
                </li>
              ))}
            </ul>
          </div>
        )}

        {bragSheetData.extracurriculars && bragSheetData.extracurriculars.length > 0 && (
          <div>
            <h4 className="font-semibold text-stone-700 mb-2">Activities</h4>
            <ul className="list-disc list-inside space-y-1">
              {bragSheetData.extracurriculars.slice(0, 3).map((activity, idx) => (
                <li key={idx} className="text-sm text-stone-600">
                  {typeof activity === 'object' ?
                    (activity.title || activity.activity || activity.name || activity.description || JSON.stringify(activity)) :
                    activity}
                </li>
              ))}
            </ul>
          </div>
        )}

        {bragSheetData.personalQualities && bragSheetData.personalQualities.length > 0 && (
          <div>
            <h4 className="font-semibold text-stone-700 mb-2">Personal Qualities</h4>
            <p className="text-sm text-stone-600">
              {bragSheetData.personalQualities.slice(0, 3).map(quality =>
                typeof quality === 'object' ?
                  (quality.quality || quality.name || quality.description || JSON.stringify(quality)) :
                  quality
              ).join(', ')}
            </p>
          </div>
        )}
      </div>
    );
  };

  const BragSheetModal = ({ item, onClose }) => {
    if (!item) return null;

    const bragSheetData = item.content || item.itemData?.brag_sheet_data || {};
    const studentName = item.student?.name || 'Student';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-violet-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-stone-900">Full Brag Sheet</h2>
                <p className="text-sm text-stone-600 mt-1">
                  {item.item_title} • Shared by {studentName}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/80 rounded-lg transition-colors"
              >
                <X size={20} className="text-stone-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
            <div className="space-y-6">
              {/* Basic Info */}
              {bragSheetData.basicInfo && (
                <div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-3">Basic Information</h3>
                  <div className="bg-stone-50 rounded-lg p-4 space-y-2">
                    <p><span className="font-medium">Name:</span> {bragSheetData.basicInfo.name || studentName}</p>
                    <p><span className="font-medium">Grade Level:</span> {bragSheetData.basicInfo.gradeLevel || 'N/A'}</p>
                    <p><span className="font-medium">School:</span> {bragSheetData.basicInfo.highSchool || 'N/A'}</p>
                    {bragSheetData.basicInfo.email && <p><span className="font-medium">Email:</span> {bragSheetData.basicInfo.email}</p>}
                  </div>
                </div>
              )}

              {/* Additional sections would go here - achievements, activities, etc. */}
              {renderBragSheetContent(bragSheetData)}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-stone-200 bg-stone-50 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-stone-700 font-medium hover:bg-stone-200 rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => setShowPrintView(true)}
              className="px-5 py-2.5 bg-indigo-600 text-white font-medium hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <Printer size={16} />
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedStudent) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          No Student Selected
        </h2>
        <p className="text-slate-600">
          Select a student to view their shared documents and provide feedback.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Shared Documents</h1>
          <p className="text-slate-600 mt-1">
            Review and provide feedback on {selectedStudent.name}'s shared work
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-500">
            {enrichedItems.length} items shared with you
          </span>
        </div>
      </div>

      {/* Shared Items List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border animate-pulse p-6">
              <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-slate-200 rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : enrichedItems.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-stone-300 mb-4" />
          <p className="text-stone-600">No items have been shared with you yet.</p>
          <p className="text-sm text-stone-500 mt-2">
            When {selectedStudent.name} shares essays, brag sheets, or other documents, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {enrichedItems.map(item => (
            <div key={item.id} className="bg-white rounded-xl border p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {item.item_type === 'essay' && <FileText className="text-azure-600" size={20} />}
                    {item.item_type.includes('brag_sheet') && <Award className="text-violet-600" size={20} />}
                    {item.item_type === 'recommendation' && <MessageSquare className="text-emerald-600" size={20} />}
                    <h3 className="text-lg font-semibold">
                      {item.item_title ||
                       (item.item_type === 'essay' && item.itemData?.prompt_type) ||
                       (item.item_type === 'teacher_brag_sheet' && `Brag Sheet for ${item.itemData?.recommender_name}`) ||
                       item.item_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h3>
                  </div>
                  <p className="text-sm text-stone-500">
                    Shared by {item.student?.name} • {' '}
                    {item.shared_at && format(new Date(item.shared_at), 'MMM d, yyyy at h:mm a')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {item.item_type.includes('brag_sheet') && (
                    <button
                      onClick={() => setShowFullBragSheet(item)}
                      className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 flex items-center gap-2 text-sm font-medium"
                    >
                      <Eye size={14} />
                      View Full
                    </button>
                  )}
                  {item.allow_comments && (
                    <span className="px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full">
                      Comments enabled
                    </span>
                  )}
                </div>
              </div>

              {/* Item Content Preview */}
              <div className="bg-stone-50 rounded-lg p-4 mb-4">
                {item.item_type === 'essay' && (
                  <div>
                    <p className="text-sm text-stone-700 line-clamp-3">
                      {item.content || 'Essay content not available'}
                    </p>
                    {item.itemData?.college && (
                      <p className="text-xs text-stone-500 mt-2">
                        {item.itemData.college.name} • {item.itemData.word_count || 0} words
                      </p>
                    )}
                  </div>
                )}

                {item.item_type.includes('brag_sheet') && (
                  <div>
                    {renderBragSheetContent(item.content || item.itemData?.brag_sheet_data)}
                  </div>
                )}

                {item.item_type === 'recommendation' && (
                  <div>
                    <p className="text-sm font-medium mb-2">Letter Request Details</p>
                    <div className="space-y-1 text-sm text-stone-600">
                      <p>Teacher: {item.itemData?.recommender_name}</p>
                      <p>Subject: {item.itemData?.subject}</p>
                      <p>Status: {item.itemData?.status?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Comments Section */}
              {item.allow_comments && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle size={18} className="text-stone-500" />
                    <h4 className="font-medium">Comments & Feedback</h4>
                  </div>

                  {item.comments && item.comments.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {item.comments.map(comment => (
                        <div key={comment.id} className={`rounded-lg p-3 ${
                          comment.author_type === 'student'
                            ? 'bg-violet-50 ml-6'
                            : 'bg-azure-50'
                        }`}>
                          <div className="text-sm whitespace-pre-wrap break-words">
                            {comment.comment_text}
                          </div>
                          <p className="text-xs text-stone-500 mt-1">
                            — {comment.author_type === 'student'
                              ? item.student?.name || 'Student'
                              : comment.parent?.name || 'You'}, {' '}
                            {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-start gap-2">
                    <textarea
                      value={commentText[item.id] || ''}
                      onChange={(e) => {
                        setCommentText(prev => ({
                          ...prev,
                          [item.id]: e.target.value
                        }));
                      }}
                      placeholder="Add encouraging feedback or suggestions..."
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-transparent resize-vertical min-h-[80px] max-h-[200px]"
                      rows={3}
                    />
                    <button
                      onClick={() => handleAddComment(item.id)}
                      disabled={!commentText[item.id]?.trim()}
                      className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:bg-stone-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    >
                      <Send size={16} />
                      Send
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Brag Sheet Modal */}
      {showFullBragSheet && !showPrintView && (
        <BragSheetModal
          item={showFullBragSheet}
          onClose={() => setShowFullBragSheet(null)}
        />
      )}

      {/* Print View */}
      {showPrintView && showFullBragSheet && (() => {
        const bragSheetData = showFullBragSheet.content || showFullBragSheet.itemData?.brag_sheet_data || {};
        const studentName = showFullBragSheet.student?.name || 'Student';
        const isTeacherSpecific = showFullBragSheet.item_type === 'teacher_brag_sheet';

        return (
          <div className="fixed inset-0 bg-white z-[60] overflow-auto">
            <div className="fixed top-4 right-4 z-[70] print:hidden flex gap-2">
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 shadow-lg"
              >
                <Printer size={16} />
                Print
              </button>
              <button
                onClick={() => setShowPrintView(false)}
                className="px-4 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 flex items-center gap-2 shadow-lg"
              >
                <X size={16} />
                Close
              </button>
            </div>
            <ProfessionalBragSheetView
              bragSheetData={bragSheetData}
              isTeacherSpecific={isTeacherSpecific}
              teacherInfo={isTeacherSpecific ? {
                name: showFullBragSheet.itemData?.recommender_name || 'Teacher',
                subject: showFullBragSheet.itemData?.subject || 'Subject',
                email: '',
                deadline: 'TBD'
              } : null}
              studentInfo={{
                email: '',
                phone: '',
                name: studentName,
                graduationYear: bragSheetData.basicInfo?.graduationYear || new Date().getFullYear() + 1,
                highSchool: bragSheetData.basicInfo?.highSchool || 'High School'
              }}
              hideActions={true}
            />
          </div>
        );
      })()}
    </div>
  );
};

export default SharedDocumentsSection;